import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Save, EyeOff, RotateCcw, Trash2, ChevronRight, Upload, X,
  Play, Image, ChevronDown, ChevronUp, Paperclip, Copy, Download,
  Database, Eye
} from "lucide-react";
import { SimulationButton } from "../components/QuestionSimulationModal";
import { copyQuestionInfo, downloadQuestionJson } from "../utils/questionHelpers";
import MediaVaultTab from "../components/MediaVaultTab";
import { adaptCenterQuestion } from "../question-studio/questionStudioModel.mjs";
import StudentQuestionRenderer from "../question-studio/StudentQuestionRenderer";
import { QuestionStudioShellWrapper } from "../question-studio/QuestionStudioShell";
import VisualGenerationPanel from "../question-studio/VisualGenerationPanel";

const ANSWER_OPTIONS = ["A", "B", "C", "D"];
const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Kolay" },
  { value: "medium", label: "Orta" },
  { value: "hard", label: "Zor" },
];
const DISPLAY_TYPE_OPTIONS = [
  { value: "", label: "— Belirtilmedi —" },
  { value: "T", label: "T — Metin" },
  { value: "R", label: "R — Görsel Soru" },
  { value: "TR", label: "TR — Görsel Şık" },
  { value: "ZTR", label: "ZTR — Görsel Soru+Şık" },
  { value: "V", label: "V — Video" },
];

const EMPTY = {
  question_code: "", archive_name: "", lesson_name: "", topic: "", subtopic: "", unit_name: "",
  question_text: "", option_a: "", option_b: "", option_c: "", option_d: "",
  correct_answer: "A", solution_text: "",
  difficulty_level: "medium", question_type: "multiple_choice", display_type: "",
  source_name: "", exam_group: "",
  taxonomy_level_1: "", taxonomy_level_2: "", taxonomy_level_3: "",
  tags: "",
  question_image_url: "", option_a_image_url: "", option_b_image_url: "",
  option_c_image_url: "", option_d_image_url: "",
  question_video_url: "", question_gif_url: "",
  animation_video_url: "", short_video_vertical_url: "", explanation_video_horizontal_url: "",
  explanation_image_url: "",
  status: "active",
};

// Media upload types that indicate media-heavy content
const MEDIA_HEAVY_TYPES = new Set(["R", "TR", "ZTR", "V"]);

// V1 DB field → V2 semantic slot (ayır işlemi için)
const V2_SLOT_BY_FIELD = {
  question_image_url: "question_image",
  question_video_url: "question_video",
  question_gif_url: "question_gif",
  option_a_image_url: "option_A",
  option_b_image_url: "option_B",
  option_c_image_url: "option_C",
  option_d_image_url: "option_D",
  animation_video_url: "question_animation",
  short_video_vertical_url: "explanation_video_vertical",
  explanation_video_horizontal_url: "explanation_video_horizontal",
  explanation_image_url: "explanation_image",
};

// Fields that count as "has media"
function hasAnyMedia(form) {
  return !!(
    form.question_image_url || form.question_video_url || form.question_gif_url ||
    form.option_a_image_url || form.option_b_image_url ||
    form.option_c_image_url || form.option_d_image_url ||
    form.animation_video_url || form.short_video_vertical_url ||
    form.explanation_video_horizontal_url || form.explanation_image_url
  );
}

function isVideoUrl(url) {
  return /\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url);
}

function getVideoProxySrc(url) {
  if (!url) return url;
  if (url.includes(".r2.dev/")) {
    const { BACKEND_URL_BASE } = require("../services/api");
    return `${BACKEND_URL_BASE}/api/media/video-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

// ── Compact video player ─────────────────────────────────────────────────────
function VideoPlayer({ src, className, testId }) {
  const [status, setStatus] = useState("loading");
  if (!src) return null;
  const proxySrc = getVideoProxySrc(src);
  return (
    <div className="relative rounded-lg overflow-hidden bg-black">
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10 rounded-lg">
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      {status === "error" ? (
        <div className="flex items-center justify-center h-20 bg-red-950/20 rounded-lg border border-red-800/30">
          <p className="text-red-400 text-xs">Video oynatılamadı</p>
        </div>
      ) : (
        <video
          key={proxySrc}
          src={proxySrc}
          controls
          autoPlay={false}
          muted
          loop={false}
          playsInline
          preload="metadata"
          className={className}
          data-testid={testId}
          onLoadStart={() => setStatus("loading")}
          onCanPlay={() => setStatus("ready")}
          onError={() => setStatus("error")}
        />
      )}
    </div>
  );
}

// ── Compact upload button for option rows ────────────────────────────────────
function OptionUploadBtn({ fieldKey, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(fieldKey, res.data.url || res.data.storage_path);
      toast.success("Görsel yüklendi");
    } catch { toast.error("Yükleme başarısız"); }
    finally { setUploading(false); }
  };
  return (
    <label className="cursor-pointer flex-shrink-0" title="Görsel ekle">
      <span className={`flex items-center justify-center w-7 h-7 rounded border transition-colors ${
        value ? "border-blue-300 bg-blue-50 text-blue-500" : "border-slate-200 bg-white text-slate-400 hover:border-blue-300 hover:text-blue-500"
      }`}>
        {uploading ? <div className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" /> : <Paperclip size={12} />}
      </span>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />
    </label>
  );
}

// ── Full media upload row (used in collapsible section) ──────────────────────
function MediaUploadRow({ label, fieldKey, value, onChange, accept }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(fieldKey, res.data.url || res.data.storage_path);
      toast.success(`${label} yüklendi`);
    } catch { toast.error("Yükleme başarısız"); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <div className="flex gap-2 items-center">
        <Input value={value} onChange={(e) => onChange(fieldKey, e.target.value)}
          placeholder="URL veya yükleyin..." className="h-8 text-xs border-slate-200 flex-1 font-mono" />
        <label>
          <Button variant="outline" size="sm" disabled={uploading} asChild className="h-8 text-xs cursor-pointer border-slate-200 shrink-0">
            <span className="flex items-center gap-1">
              <Upload size={11} /> {uploading ? "..." : "Yükle"}
            </span>
          </Button>
          <input type="file" accept={accept} className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange(fieldKey, "")} className="p-1.5 text-slate-400 hover:text-red-500 rounded" title="Sorudan ayır; dosyayı kasada koru">
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Rich Text Editor ─────────────────────────────────────────────────────────
const FORMAT_BTNS = [
  { cmd: "bold",        label: "B",  title: "Kalın",         cls: "font-bold" },
  { cmd: "italic",      label: "I",  title: "İtalik",        cls: "italic" },
  { cmd: "underline",   label: "U",  title: "Altı Çizili",   cls: "underline" },
];

function RichToolbar({ editorRef, onInput, mini = false }) {
  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    setTimeout(() => onInput && onInput(), 0);
  };
  return (
    <div className={`flex items-center gap-0.5 bg-slate-50 border-b border-slate-200 ${mini ? "px-1.5 py-0.5" : "px-2 py-1"}`}>
      {FORMAT_BTNS.map(({ cmd, label, title, cls }) => (
        <button key={cmd} type="button" title={title} onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
          className={`${mini ? "w-5 h-5 text-[10px]" : "w-6 h-6 text-xs"} rounded hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors ${cls}`}>
          {label}
        </button>
      ))}
      {!mini && (
        <>
          <span className="w-px h-3.5 bg-slate-200 mx-0.5" />
          <button type="button" title="Alt simge" onMouseDown={(e) => { e.preventDefault(); exec("subscript"); }}
            className="w-6 h-6 text-[10px] rounded hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            x₂
          </button>
          <button type="button" title="Üst simge" onMouseDown={(e) => { e.preventDefault(); exec("superscript"); }}
            className="w-6 h-6 text-[10px] rounded hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            x²
          </button>
        </>
      )}
    </div>
  );
}

function RichTextArea({ value, onChange, placeholder, minRows = 5, testId }) {
  const editorRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (editorRef.current && isFirstRender.current) {
      editorRef.current.innerHTML = value || "";
      isFirstRender.current = false;
    }
  }, []); // eslint-disable-line

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || "");
  }, [onChange]);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-blue-400 focus-within:border-blue-400">
      <RichToolbar editorRef={editorRef} onInput={handleInput} />
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-testid={testId}
        onInput={handleInput}
        className="px-3 py-2 text-sm text-slate-800 focus:outline-none leading-relaxed"
        style={{ minHeight: `${minRows * 1.75}rem`, whiteSpace: "pre-wrap" }}
        data-placeholder={placeholder}
      />
    </div>
  );
}

// CSS for placeholder
const richEditorStyle = `
  [contenteditable][data-placeholder]:empty::before {
    content: attr(data-placeholder);
    color: #94a3b8;
    pointer-events: none;
  }
`;

function RichOptionInput({ value, onChange, placeholder, testId }) {
  const editorRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (editorRef.current && isFirstRender.current) {
      editorRef.current.innerHTML = value || "";
      isFirstRender.current = false;
    }
  }, []); // eslint-disable-line

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || "");
  }, [onChange]);

  return (
    <div className="flex-1 border border-slate-200 rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-blue-400 focus-within:border-blue-400">
      <RichToolbar editorRef={editorRef} onInput={handleInput} mini />
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-testid={testId}
        onInput={handleInput}
        className="px-2.5 py-1.5 text-sm text-slate-800 focus:outline-none"
        style={{ minHeight: "2rem", whiteSpace: "pre-wrap" }}
        data-placeholder={placeholder}
      />
    </div>
  );
}

// ── CascadeSelect: dropdown with "no children" fallback ──────────────────────
function CascadeSelect({ label, value, options, parentValue, parentLabel, onChange, testId }) {
  const noParent = !parentValue;
  const noOptions = parentValue && options.length === 0;

  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      {noParent ? (
        <p className="text-[10px] text-slate-400 italic py-1.5">Önce {parentLabel} seçin</p>
      ) : noOptions ? (
        <div className="flex items-center gap-1.5">
          <Input value={value} onChange={(e) => onChange(e.target.value)}
            placeholder={`${label} girin...`} data-testid={testId} className="h-8 text-xs border-amber-200 bg-amber-50/40" />
          <span className="text-[10px] text-amber-600 whitespace-nowrap">Bağlı kayıt yok</span>
        </div>
      ) : (
        <select value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="">— {label} seçin —</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
    </div>
  );
}

// ── Section header ───────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
      {children}
    </p>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function QuestionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [question, setQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState("form"); // "form" | "vault" | "studio"
  const [archives, setArchives] = useState([]);
  const [overrideV2MediaSlots, setOverrideV2MediaSlots] = useState([]);

  // Stüdyo Layout Feature Flag (USE_QUESTION_STUDIO_LAYOUT)
  const [studioLayout, setStudioLayout] = useState(
    localStorage.getItem("USE_QUESTION_STUDIO_LAYOUT") === "1"
  );
  // isStudio: Sadece mevcut (yeni değil) sorular için stüdyo düzeni aktif
  const isStudio = studioLayout && !isNew;

  // Adım 5: "Bu soruda dene" aday medya state — yalnızca frontend sandbox, API yazma yok
  const [trialCandidate, setTrialCandidate] = useState(null);

  // Cascade taxonomy options
  const [lessonOpts, setLessonOpts] = useState([]);
  const [topicOpts, setTopicOpts] = useState([]);
  const [subtopicOpts, setSubtopicOpts] = useState([]);
  const [unitOpts, setUnitOpts] = useState([]);
  const [tax1Opts, setTax1Opts] = useState([]);
  const [cascLoading, setCascLoading] = useState(false);

  // Medya yükleme bloğu: medya varsa veya görsel/video tip seçiliyse otomatik açık
  const mediaAutoOpen = useMemo(() =>
    hasAnyMedia(form) || MEDIA_HEAVY_TYPES.has(form.display_type),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // sadece ilk render'da hesapla
  );
  const [mediaOpen, setMediaOpen] = useState(false); // başlangıç false, sonra effect günceller

  useEffect(() => {
    api.get("/archives/names").then((r) => setArchives(r.data.names)).catch(() => {});
    api.get("/questions/meta/filters").then((r) => setLessonOpts(r.data.lesson_names || [])).catch(() => {});
  }, []);

  // Fetch children for a taxonomy level
  const fetchChildren = useCallback(async (parentType, parentValue, childType, setter) => {
    if (!parentValue) { setter([]); return; }
    setCascLoading(true);
    try {
      const res = await api.get("/taxonomy/children", {
        params: { parent_type: parentType, parent_value: parentValue, child_type: childType },
      });
      setter(res.data || []);
    } catch { setter([]); }
    finally { setCascLoading(false); }
  }, []);

  // Cascade effects: only FETCH options, never reset form values
  // (Resets happen in the onChange handlers of each field)
  useEffect(() => {
    if (form.lesson_name) fetchChildren("lesson_name", form.lesson_name, "topic", setTopicOpts);
    else { setTopicOpts([]); setSubtopicOpts([]); setUnitOpts([]); setTax1Opts([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.lesson_name]);

  useEffect(() => {
    if (form.topic) fetchChildren("topic", form.topic, "subtopic", setSubtopicOpts);
    else { setSubtopicOpts([]); setUnitOpts([]); setTax1Opts([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.topic]);

  useEffect(() => {
    if (form.subtopic) fetchChildren("subtopic", form.subtopic, "unit_name", setUnitOpts);
    else { setUnitOpts([]); setTax1Opts([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subtopic]);

  useEffect(() => {
    if (form.unit_name) fetchChildren("unit_name", form.unit_name, "taxonomy_level_1", setTax1Opts);
    else setTax1Opts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.unit_name]);

  useEffect(() => {
    if (!isNew) {
      api.get(`/questions/${id}`)
        .then((r) => {
          const q = r.data;
          setQuestion(q);
          const filled = { ...q, tags: Array.isArray(q.tags) ? q.tags.join(", ") : (q.tags || "") };
          setForm(filled);
          setOverrideV2MediaSlots([]);
          // Pre-load cascade options for existing data
          if (filled.lesson_name) fetchChildren("lesson_name", filled.lesson_name, "topic", setTopicOpts);
          if (filled.topic) fetchChildren("topic", filled.topic, "subtopic", setSubtopicOpts);
          if (filled.subtopic) fetchChildren("subtopic", filled.subtopic, "unit_name", setUnitOpts);
          if (filled.unit_name) fetchChildren("unit_name", filled.unit_name, "taxonomy_level_1", setTax1Opts);
          // Medya varsa veya görsel/video tipiyse bloğu aç
          setMediaOpen(hasAnyMedia(filled) || MEDIA_HEAVY_TYPES.has(filled.display_type));
        })
        .catch(() => { toast.error("Soru yüklenemedi"); navigate("/questions"); })
        .finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  // display_type değişince medya bloğunu otomatik aç
  useEffect(() => {
    if (MEDIA_HEAVY_TYPES.has(form.display_type)) setMediaOpen(true);
  }, [form.display_type]);

  const set = (field, val) => {
    setForm((f) => ({ ...f, [field]: val }));
    const slot = V2_SLOT_BY_FIELD[field];
    if (slot) {
      setOverrideV2MediaSlots((current) => current.includes(slot) ? current : [...current, slot]);
    }
  };

  const detachQuestionMedia = (field, label) => {
    if (!window.confirm(`${label} sorudan ayrılsın mı? Dosya medya kasasında korunacak.`)) return;
    set(field, "");
    toast.info("Medya sorudan ayrıldı. Kalıcı olması için soruyu kaydedin.");
  };

  // Quick upload handlers for SORU İÇERİĞİ section
  const [quickImgUploading, setQuickImgUploading] = useState(false);
  const [quickVidUploading, setQuickVidUploading] = useState(false);

  // Kalite öneri state'i
  const [qualitySuggestion, setQualitySuggestion] = useState(null);
  const [applyingQuality, setApplyingQuality] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/quality/suggestions/question/${id}`)
      .then(r => setQualitySuggestion(r.data.suggestion || null))
      .catch(() => {});
  }, [id]);

  const handleApplyQuality = async () => {
    if (!qualitySuggestion) return;
    setApplyingQuality(true);
    try {
      await api.patch(`/quality/apply/${qualitySuggestion.id}`);
      const res = await api.get(`/questions/${id}`);
      const q = res.data;
      setForm(f => ({ ...f, question_text: q.question_text }));
      setQualitySuggestion(null);
      toast.success("Düzeltme uygulandı");
    } catch { toast.error("Uygulama başarısız"); }
    finally { setApplyingQuality(false); }
  };

  const handleDismissQuality = async () => {
    if (!qualitySuggestion) return;
    try {
      await api.delete(`/quality/suggestions/${qualitySuggestion.id}`);
      setQualitySuggestion(null);
      toast.info("Öneri atlandı");
    } catch { toast.error("İşlem başarısız"); }
  };

  const copyCodeNumber = async () => {
    const num = form.question_code?.match(/\d+/)?.[0];
    if (num) {
      try {
        await navigator.clipboard.writeText(num);
        toast.info(`"${num}" kopyalandı — klasörde Ctrl+V ile yapıştırın`);
      } catch { /* clipboard permission denied, silent */ }
    }
  };

  const handleQuickUpload = async (file, fieldKey, setLoading) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(fieldKey, res.data.url || res.data.storage_path);
      setMediaOpen(true);
      toast.success("Yüklendi");
    } catch { toast.error("Yükleme başarısız"); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        question_code: form.question_code.trim().toUpperCase(),
        correct_answer: form.correct_answer.toUpperCase(),
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      if (isNew) {
        const res = await api.post("/questions", payload);
        toast.success("Soru oluşturuldu");
        navigate(`/questions/${res.data.id}`);
      } else {
        if (!isNew && overrideV2MediaSlots.length) {
          payload.override_v2_media_slots = overrideV2MediaSlots;
        }
        const res = await api.put(`/questions/${id}`, payload);
        const saved = res.data;
        setQuestion(saved);
        setForm({ ...saved, tags: Array.isArray(saved.tags) ? saved.tags.join(", ") : (saved.tags || "") });
        setOverrideV2MediaSlots([]);
        toast.success("Soru güncellendi");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassive = async () => {
    if (!window.confirm("Bu soruyu pasife almak istiyor musunuz?")) return;
    try { await api.post(`/questions/${id}/set-passive`); toast.success("Soru pasife alındı"); navigate("/questions"); }
    catch { toast.error("İşlem başarısız"); }
  };

  const handleRestore = async () => {
    try { await api.post(`/questions/${id}/restore`); toast.success("Soru aktif edildi"); navigate("/questions"); }
    catch { toast.error("İşlem başarısız"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Kalıcı olarak silmek istediğinizden emin misiniz?")) return;
    try { await api.delete(`/questions/${id}`); toast.success("Soru silindi"); navigate("/questions"); }
    catch { toast.error("Silme başarısız"); }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />)}
      </div>
    );
  }

  const hasMainMedia = form.question_image_url || form.question_video_url || form.question_gif_url;

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Inject rich editor placeholder CSS */}
      <style>{richEditorStyle}</style>
      <form onSubmit={handleSave}>

        {/* ── KALİTE ÖNERİ BANNER ── */}
        {qualitySuggestion && (
          <div className="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="quality-banner">
            <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-800 mb-1">AI Düzeltme Önerisi</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {(qualitySuggestion.issues || []).map((issue, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                    {issue.description}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-600 line-clamp-2">{(qualitySuggestion.suggested_text_plain || "").substring(0, 200)}...</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={handleDismissQuality}
                className="text-xs px-2.5 py-1.5 border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-100 transition-colors"
                data-testid="quality-banner-dismiss"
              >
                Atla
              </button>
              <button
                type="button"
                onClick={handleApplyQuality}
                disabled={applyingQuality}
                className="text-xs px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                data-testid="quality-banner-apply"
              >
                {applyingQuality ? "Uygulanıyor..." : "Uygula"}
              </button>
            </div>
          </div>
        )}

        {/* ── TOP BAR ── */}
        <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between gap-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-slate-400 min-w-0">
            <button type="button" onClick={() => navigate("/questions")} className="hover:text-blue-600 transition-colors shrink-0">Sorular</button>
            <ChevronRight size={11} />
            <span className="text-slate-700 font-semibold truncate">{isNew ? "Yeni Soru" : form.question_code || id}</span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {!isNew && <SimulationButton question={question || form} size="md" />}
            {!isNew && (
              <Button type="button" variant="outline" size="sm"
                onClick={() => copyQuestionInfo(question || form)}
                data-testid="copy-info-btn"
                className="h-8 text-xs gap-1.5 text-violet-600 border-violet-200 hover:bg-violet-50">
                <Copy size={12} /> Kopyala
              </Button>
            )}
            {!isNew && (
              <Button type="button" variant="outline" size="sm"
                onClick={() => downloadQuestionJson(question || form)}
                data-testid="download-json-btn"
                className="h-8 text-xs gap-1.5 text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                <Download size={12} /> JSON İndir
              </Button>
            )}
            {!isNew && question?.status === "active" && (
              <Button type="button" variant="outline" size="sm" onClick={handleSetPassive} data-testid="set-passive-btn"
                className="h-8 text-xs gap-1.5 text-amber-600 border-amber-200 hover:bg-amber-50">
                <EyeOff size={12} /> Pasife Al
              </Button>
            )}
            {!isNew && question?.status === "passive" && (
              <>
                <Button type="button" variant="outline" size="sm" onClick={handleRestore} data-testid="restore-btn"
                  className="h-8 text-xs gap-1.5 text-green-600 border-green-200 hover:bg-green-50">
                  <RotateCcw size={12} /> Aktife Al
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleDelete} data-testid="delete-btn"
                  className="h-8 text-xs gap-1.5 text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 size={12} /> Sil
                </Button>
              </>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/questions")} data-testid="cancel-btn"
              className="h-8 text-xs border-slate-200 text-slate-500">
              İptal
            </Button>
            {/* Stüdyo Layout Toggle */}
            {!isNew && (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const next = !studioLayout;
                  setStudioLayout(next);
                  localStorage.setItem("USE_QUESTION_STUDIO_LAYOUT", next ? "1" : "0");
                }}
                data-testid="studio-layout-toggle"
                title={studioLayout ? "Stüdyo düzenini kapat" : "Stüdyo düzenini aç (USE_QUESTION_STUDIO_LAYOUT)"}
                className={`h-8 text-xs gap-1.5 ${studioLayout ? "bg-indigo-600 hover:bg-indigo-700 text-white border-0" : "border-slate-200 text-slate-500 bg-white hover:bg-slate-50"}`}
                variant={studioLayout ? "default" : "outline"}
              >
                <Eye size={12} />
                {studioLayout ? "Stüdyo Açık" : "Stüdyo"}
              </Button>
            )}
            <Button type="submit" disabled={saving} data-testid="save-question-btn"
              className="h-8 text-xs px-4 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
              <Save size={12} />
              {saving ? "Kaydediliyor..." : isNew ? "Oluştur" : "Kaydet"}
            </Button>
          </div>
        </div>

        {/* ══ STÜDYO LAYOUT SARMALI ══
             isStudio=false → children doğrudan döndürülür (mevcut davranış değişmez)
             isStudio=true  → QuestionStudioShell ile 3-panel düzenine sarılır      */}
        <QuestionStudioShellWrapper
          isStudio={isStudio}
          leftPanel={
            <div className="p-4">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
                Kaydedilmemiş değişiklikler anında yansır. Yayın durumu değişmez.
              </p>
              <StudentQuestionRenderer
                question={adaptCenterQuestion(form)}
                trialCandidate={trialCandidate}
                onRemoveTrial={() => setTrialCandidate(null)}
              />
            </div>
          }
          rightPanel={
            <div className="p-3 space-y-3">
              <MediaVaultTab
                questionId={id}
                questionData={form}
                compact
                onTryBinding={(c) => setTrialCandidate(c)}
              />
              <VisualGenerationPanel
                provider={null}
                onTryOnQuestion={(job) => {
                  // Sağlayıcı bağlıyken: üretim iş talebini yerel deneme adayı olarak işle.
                  // job.contract === 'candidate_only' garantisi.
                  if (job) setTrialCandidate(job);
                }}
                onAttachToDraft={(job) => {
                  // Sağlayıcı bağlıyken: taslak bağlama wizard'ı için iş talebi kaydedilir.
                  // Şu an provider null — bu handler hiç çalışmaz.
                  if (job) console.info('[VisualGen] Taslağa bağla:', job.usage_key, job.action);
                }}
              />
            </div>
          }
        >

        {/* ── SEKME ÇUBUĞU — stüdyo modunda gizlenir ── */}
        {!isNew && !isStudio && (
          <div className="sticky top-[53px] z-10 bg-white border-b border-slate-200 px-6 flex gap-0">
            {[
              { key: "form",   label: "Soru Formu" },
              { key: "vault",  label: "Media V2 Kasası", icon: <Database size={11} className="inline mr-1" /> },
              { key: "studio", label: "Öğrenci Görünümü", icon: <Eye size={11} className="inline mr-1" /> },
            ].map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.key
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Media V2 Kasası Sekmesi — stüdyo modunda gizlenir (sağ panelde görünür) ── */}
        {!isNew && !isStudio && activeTab === "vault" && (
          <div className="max-w-5xl mx-auto px-6 py-6">
            <MediaVaultTab questionId={id} questionData={form} />
          </div>
        )}

        {/* ── Öğrenci Görünümü Sekmesi — stüdyo modunda gizlenir (sol panelde görünür) ── */}
        {/* KURAL: Bu sekme hiçbir API yazma çağrısı yapmaz. Yalnızca form state'i canlı önizler. */}
        {!isNew && !isStudio && activeTab === "studio" && (
          <div className="max-w-lg mx-auto px-6 py-6">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Kaydedilmemiş değişiklikler anında yansır. Yayın durumu değişmez.
              </p>
            </div>
            <StudentQuestionRenderer
              question={adaptCenterQuestion(form)}
              trialCandidate={trialCandidate}
              onRemoveTrial={() => setTrialCandidate(null)}
            />
          </div>
        )}

        {/* ── Soru Formu — stüdyo modunda HER ZAMAN görünür, form sekmesinde ise conditional ── */}
        {(isNew || isStudio || activeTab === "form") && (
        <div className={`${isStudio ? "" : "max-w-5xl mx-auto "}px-6 py-6 space-y-5`}>

          {/* ── BLOK 1: KİMLİK + SINIFLANDIRMA ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <SectionTitle>Kimlik & Sınıflandırma</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Soru Kodu *</Label>
                <Input value={form.question_code} onChange={(e) => set("question_code", e.target.value)}
                  placeholder="SR-001" required data-testid="question-code-input"
                  className="h-8 text-xs font-mono border-slate-200" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Arşiv</Label>
                <select value={form.archive_name} onChange={(e) => set("archive_name", e.target.value)}
                  data-testid="archive-select"
                  className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">— Arşiv —</option>
                  {archives.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Ders</Label>
                {lessonOpts.length > 0 ? (
                  <select value={form.lesson_name}
                    onChange={(e) => setForm((f) => ({ ...f, lesson_name: e.target.value, topic: "", subtopic: "", unit_name: "", taxonomy_level_1: "" }))}
                    data-testid="lesson-name-input-header"
                    className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">— Ders —</option>
                    {lessonOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input value={form.lesson_name} onChange={(e) => set("lesson_name", e.target.value)}
                    placeholder="Ders adı girin" data-testid="lesson-name-input-header" className="h-8 text-xs border-slate-200" />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Zorluk</Label>
                <select value={form.difficulty_level} onChange={(e) => set("difficulty_level", e.target.value)}
                  data-testid="difficulty-select"
                  className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  {DIFFICULTY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Sunum Tipi</Label>
                <select value={form.display_type} onChange={(e) => set("display_type", e.target.value)}
                  data-testid="display-type-select"
                  className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  {DISPLAY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Durum</Label>
                <select value={form.status} onChange={(e) => set("status", e.target.value)}
                  data-testid="status-select"
                  className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="active">Aktif</option>
                  <option value="passive">Pasif</option>
                  <option value="draft">Taslak</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── BLOK 2: SORU İÇERİĞİ + MEDYA ÖNİZLEME ── */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className={`${hasMainMedia ? "lg:grid lg:grid-cols-5" : ""}`}>
              {/* Sol: Soru içeriği */}
              <div className={`${hasMainMedia ? "lg:col-span-3 border-r border-slate-100" : ""} p-5`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Soru İçeriği</p>
                  <div className="flex items-center gap-1.5">
                    {/* Hızlı görsel yükle */}
                    <label className="cursor-pointer" title="Soru Görseli Yükle (R tipi)" onClick={copyCodeNumber}>
                      <span className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                        form.question_image_url
                          ? "border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100"
                          : "border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:text-blue-600"
                      }`}>
                        {quickImgUploading
                          ? <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin" />
                          : <Image size={11} />}
                        {form.question_image_url ? "Görseli Değiştir" : "Görsel Yükle"}
                      </span>
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => handleQuickUpload(e.target.files[0], "question_image_url", setQuickImgUploading)} />
                    </label>
                    {/* Hızlı video yükle */}
                    <label className="cursor-pointer" title="Soru Videosu Yükle (V tipi)" onClick={copyCodeNumber}>
                      <span className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                        form.question_video_url
                          ? "border-orange-300 bg-orange-50 text-orange-600 hover:bg-orange-100"
                          : "border-slate-200 bg-white text-slate-500 hover:border-orange-300 hover:text-orange-600"
                      }`}>
                        {quickVidUploading
                          ? <div className="w-3 h-3 border border-orange-400 border-t-transparent rounded-full animate-spin" />
                          : <Play size={11} />}
                        {form.question_video_url ? "Videoyu Değiştir" : "Video Yükle"}
                      </span>
                      <input type="file" accept="video/*" className="hidden"
                        onChange={(e) => handleQuickUpload(e.target.files[0], "question_video_url", setQuickVidUploading)} />
                    </label>
                    {form.question_video_url && (
                      <button type="button"
                        onClick={() => detachQuestionMedia("question_video_url", "Soru videosu")}
                        className="flex items-center gap-1 px-2 py-1 rounded border border-red-200 bg-red-50 text-[11px] font-medium text-red-600 hover:bg-red-100"
                        title="Videoyu sorudan ayır; dosyayı medya kasasında koru">
                        <X size={11} /> Sorudan ayır
                      </button>
                    )}
                  </div>
                </div>

                {/* Soru metni — rich text */}
                <div className="mb-4">
                  <RichTextArea
                    value={form.question_text}
                    onChange={(v) => set("question_text", v)}
                    placeholder="Soru metnini buraya yazın..."
                    minRows={6}
                    testId="question-text-input"
                  />
                </div>

                {/* Seçenekler */}
                <div className="space-y-1 mb-4">
                  {["a", "b", "c", "d"].map((opt) => {
                    const isCorrect = form.correct_answer === opt.toUpperCase();
                    return (
                      <div key={opt} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
                        {/* Doğru cevap seçici (daire badge) */}
                        <button
                          type="button"
                          onClick={() => set("correct_answer", opt.toUpperCase())}
                          data-testid={`answer-${opt.toUpperCase()}-btn`}
                          title="Doğru cevap olarak işaretle"
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                            isCorrect
                              ? "bg-teal-500 text-white shadow-sm shadow-teal-200"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {opt.toUpperCase()}
                        </button>
                        {/* Option rich text input */}
                        <RichOptionInput
                          value={form[`option_${opt}`]}
                          onChange={(v) => set(`option_${opt}`, v)}
                          placeholder={`Seçenek ${opt.toUpperCase()}`}
                          testId={`option-${opt}-input`}
                        />
                        {/* Option image thumb (if set) */}
                        {form[`option_${opt}_image_url`] && (
                          <img
                            src={form[`option_${opt}_image_url`]}
                            alt={`${opt.toUpperCase()} şık görseli`}
                            data-testid={`option-${opt}-image-preview`}
                            className="h-8 w-8 rounded object-cover border border-slate-200 shrink-0"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        {/* Upload button */}
                        <OptionUploadBtn
                          fieldKey={`option_${opt}_image_url`}
                          value={form[`option_${opt}_image_url`]}
                          onChange={set}
                        />
                        {/* Clear option image */}
                        {form[`option_${opt}_image_url`] && (
                          <button type="button" onClick={() => set(`option_${opt}_image_url`, "")}
                            className="text-slate-300 hover:text-red-400 transition-colors shrink-0">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Doğru cevap göstergesi */}
                <div className="flex items-center gap-2 mb-4 p-2 bg-teal-50 rounded-lg border border-teal-100">
                  <span className="text-xs text-teal-600 font-medium">Doğru cevap:</span>
                  <span className="w-6 h-6 rounded-full bg-teal-500 text-white text-xs font-bold flex items-center justify-center">
                    {form.correct_answer || "—"}
                  </span>
                  <span className="text-xs text-teal-500 ml-1">
                    (seçeneğin dairesine tıklayarak değiştirin)
                  </span>
                </div>

                {/* Çözüm */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Çözüm / Açıklama</Label>
                  <Textarea value={form.solution_text} onChange={(e) => set("solution_text", e.target.value)}
                    placeholder="Doğru cevabın çözümü..." rows={2}
                    data-testid="solution-text-input" className="text-sm border-slate-200 resize-none" />
                </div>
              </div>

              {/* Sağ: Medya önizleme (sadece medya varsa göster) */}
              {hasMainMedia && (
                <div className="lg:col-span-2 p-5 bg-slate-50/50">
                  <SectionTitle>Medya Önizleme</SectionTitle>
                  <div className="space-y-3" data-testid="question-media-preview">
                    {form.question_video_url && (
                      <VideoPlayer
                        src={form.question_video_url}
                        className="max-h-64 w-full object-contain rounded"
                        testId="question-video-preview"
                      />
                    )}
                    {form.question_image_url && !form.question_video_url && (
                      <img src={form.question_image_url} alt="Soru görseli"
                        className="max-h-64 rounded-lg object-contain w-full border border-slate-200 bg-white"
                        data-testid="question-image-preview"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    )}
                    {form.question_gif_url && (
                      <img src={form.question_gif_url} alt="GIF"
                        className="max-h-48 rounded-lg object-contain w-full border border-slate-200 bg-white"
                        data-testid="question-gif-preview"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    )}
                    <p className="text-xs text-slate-400 text-center">
                      {form.question_video_url ? <span className="flex items-center justify-center gap-1"><Play size={10} /> Video</span>
                        : <span className="flex items-center justify-center gap-1"><Image size={10} /> Görsel</span>}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── BLOK 3: TAKSONOMİ GRUBU ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <SectionTitle>Taksonomi & Sınıflandırma</SectionTitle>
              {cascLoading && (
                <span className="text-[10px] text-blue-500 flex items-center gap-1">
                  <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin" />
                  Yükleniyor...
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">

              {/* Ders — dropdown from taxonomy lesson_names */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Ders</Label>
                {lessonOpts.length > 0 ? (
                  <select value={form.lesson_name}
                    onChange={(e) => setForm((f) => ({ ...f, lesson_name: e.target.value, topic: "", subtopic: "", unit_name: "", taxonomy_level_1: "" }))}
                    data-testid="lesson-name-input"
                    className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">— Ders seçin —</option>
                    {lessonOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input value={form.lesson_name} onChange={(e) => setForm((f) => ({ ...f, lesson_name: e.target.value, topic: "", subtopic: "", unit_name: "", taxonomy_level_1: "" }))}
                    placeholder="Ders adı girin" data-testid="lesson-name-input" className="h-8 text-xs border-slate-200" />
                )}
              </div>

              {/* Konu — cascaded from Ders */}
              <CascadeSelect
                label="Konu"
                value={form.topic}
                options={topicOpts}
                parentValue={form.lesson_name}
                parentLabel="Ders"
                onChange={(v) => setForm((f) => ({ ...f, topic: v, subtopic: "", unit_name: "", taxonomy_level_1: "" }))}
                testId="topic-input"
              />

              {/* Alt Konu — cascaded from Konu */}
              <CascadeSelect
                label="Alt Konu"
                value={form.subtopic}
                options={subtopicOpts}
                parentValue={form.topic}
                parentLabel="Konu"
                onChange={(v) => setForm((f) => ({ ...f, subtopic: v, unit_name: "", taxonomy_level_1: "" }))}
                testId="subtopic-input"
              />

              {/* Ünite — cascaded from Alt Konu */}
              <CascadeSelect
                label="Ünite"
                value={form.unit_name}
                options={unitOpts}
                parentValue={form.subtopic}
                parentLabel="Alt Konu"
                onChange={(v) => setForm((f) => ({ ...f, unit_name: v, taxonomy_level_1: "" }))}
                testId="unit-name-input"
              />

              {/* Taksonomi 1 — cascaded from Ünite */}
              <CascadeSelect
                label="Taksonomi Düzey 1"
                value={form.taxonomy_level_1}
                options={tax1Opts}
                parentValue={form.unit_name}
                parentLabel="Ünite"
                onChange={(v) => set("taxonomy_level_1", v)}
                testId="taxonomy-1-input"
              />

              {/* Taksonomi 2 ve 3 — serbest metin */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Taksonomi Düzey 2</Label>
                <Input value={form.taxonomy_level_2} onChange={(e) => set("taxonomy_level_2", e.target.value)}
                  placeholder="Düzey 2" data-testid="taxonomy-2-input" className="h-8 text-xs border-slate-200" />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Taksonomi Düzey 3</Label>
                <Input value={form.taxonomy_level_3} onChange={(e) => set("taxonomy_level_3", e.target.value)}
                  placeholder="Düzey 3" data-testid="taxonomy-3-input" className="h-8 text-xs border-slate-200" />
              </div>
            </div>
          </div>

          {/* ── BLOK 4: KAYNAK & META ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <SectionTitle>Kaynak & Meta</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Kaynak</Label>
                <Input value={form.source_name} onChange={(e) => set("source_name", e.target.value)}
                  placeholder="Kaynak girin" data-testid="source-name-input" className="h-8 text-xs border-slate-200" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Sınav Grubu</Label>
                <Input value={form.exam_group} onChange={(e) => set("exam_group", e.target.value)}
                  placeholder="Sınav grubu girin" data-testid="exam-group-input" className="h-8 text-xs border-slate-200" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Etiketler</Label>
                <Input value={form.tags} onChange={(e) => set("tags", e.target.value)}
                  placeholder="Etiketleri virgülle girin" data-testid="tags-input" className="h-8 text-xs border-slate-200" />
              </div>
            </div>
          </div>

          {/* ── BLOK 5: MEDYA YÜKLEME (collapse) ── */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setMediaOpen((v) => !v)}
              data-testid="media-section-toggle"
              className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Medya Yükleme</p>
                {hasAnyMedia(form) && (
                  <span className="text-[10px] bg-blue-100 text-blue-600 font-semibold px-1.5 py-0.5 rounded">
                    {[
                      form.question_image_url, form.question_video_url, form.question_gif_url,
                      form.option_a_image_url, form.option_b_image_url,
                      form.option_c_image_url, form.option_d_image_url,
                      form.animation_video_url, form.short_video_vertical_url,
                      form.explanation_video_horizontal_url, form.explanation_image_url,
                    ].filter(Boolean).length} dosya
                  </span>
                )}
              </div>
              {mediaOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
            </button>

            {mediaOpen && (
              <div className="px-5 pb-5 border-t border-slate-100">
                <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: "question_image_url",              label: "Soru Görseli (R tipi)",          accept: "image/*" },
                    { key: "question_video_url",              label: "Soru Videosu (V tipi)",           accept: "video/*" },
                    { key: "question_gif_url",                label: "Soru GIF",                        accept: "image/gif" },
                    { key: "option_a_image_url",              label: "A Şık Görseli (TR/ZTR tipi)",     accept: "image/*" },
                    { key: "option_b_image_url",              label: "B Şık Görseli (TR/ZTR tipi)",     accept: "image/*" },
                    { key: "option_c_image_url",              label: "C Şık Görseli (TR/ZTR tipi)",     accept: "image/*" },
                    { key: "option_d_image_url",              label: "D Şık Görseli (TR/ZTR tipi)",     accept: "image/*" },
                    { key: "animation_video_url",             label: "Animasyon Videosu",               accept: "video/*" },
                    { key: "short_video_vertical_url",        label: "Kısa Dikey Video",                accept: "video/*" },
                    { key: "explanation_video_horizontal_url",label: "Yatay Açıklama Videosu",          accept: "video/*" },
                    { key: "explanation_image_url",           label: "Açıklama Görseli",                accept: "image/*" },
                  ].map(({ key, label, accept }) => (
                    <MediaUploadRow key={key} label={label} fieldKey={key}
                      value={form[key]} onChange={set} accept={accept} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── KAYDET ── */}
          <div className="flex items-center gap-3 pb-8">
            <Button type="submit" disabled={saving} data-testid="save-question-btn-bottom"
              className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-6 text-sm gap-2">
              <Save size={14} />
              {saving ? "Kaydediliyor..." : isNew ? "Soruyu Oluştur" : "Değişiklikleri Kaydet"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/questions")}
              className="h-9 px-4 text-sm border-slate-200 text-slate-600">
              İptal
            </Button>
          </div>
        </div>
        )} {/* isNew || isStudio || activeTab === "form" kapanış */}

        </QuestionStudioShellWrapper>
        {/* ═════════════════════════════════════════════════════════════ */}
      </form>
    </div>
  );
}
